import { Component, Input, ViewChild } from '@angular/core';
import { LabeledInputComponent } from "../../../../../shared/components/labeled-input/labeled-input.component";
import { CustomButtonComponent } from "../../../../../shared/components/custom-button/custom-button.component";
import { CommonModule } from '@angular/common';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { CommonValidatorService } from '../../../../../core/services/validators/common-validator.service';
import { Location, BaseLocation } from '../../../../../core/interfaces/location';
import { LocationService } from '../../../../../core/api/location.service';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import { DateConversionService } from '../../../../../core/services/date-conversion.service';
import { ApiErrorService } from '../../../../../core/api/api-error.service';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastComponent } from '../../../../../shared/components/toast/toast.component';
import { FormsModule } from '@angular/forms';
import { CalendarModule } from 'primeng/calendar';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { CustomDateInputComponent } from "../../../../../shared/components/custom-date-input/custom-date-input.component";
import { CreateLocationDialogComponent } from "../create-location-dialog/create-location-dialog.component";
import { CustomNumberInputComponent } from "../../../../../shared/components/custom-number-input/custom-number-input.component";
import { InvoiceService } from '../../../../../core/api/invoice.service';
import { ElectricityInvoice, GasInvoice, LocationViewUpdatePDR, LocationViewUpdatePOD } from '../../../../../core/interfaces/invoice-interfaces';
import { RouterLink } from '@angular/router';

@Component({
  selector: 'app-add-new-location-dialog',
  standalone: true,
  imports: [LabeledInputComponent, CustomButtonComponent, CommonModule, TranslocoPipe, CalendarModule, FormsModule, DialogModule, ButtonModule, CustomDateInputComponent, CreateLocationDialogComponent, CustomNumberInputComponent, RouterLink],
  templateUrl: './add-new-location-dialog.component.html',
  styleUrl: './add-new-location-dialog.component.css'
})
export class AddNewLocationDialogComponent {
  visible: boolean = false;

  // Text Inputs
  streetInput: string = '';
  streetInputError: string | null = null;

  streetNumberInput: string = '';
  streetNumberInputError: string | null = null;

  postalCodeInput: string = '';
  postalCodeInputError: string | null = null;

  //
  cityInput: string = '';
  cityInputError: string | null = null;

  provinceInput: string = '';
  provinceInputError: string | null = null;

  electricityInputs: {
    pod_id: string;
    supplier_id: string;
    supplierInput: string;
    exciseTaxesInput: string;
    podIdInput: string;
    vatInput: string;
    availableCapacityInput: string;
    startOfSupplyDate: Date | undefined;
    tensionInput: string;
    endOfSupplyDate: Date | undefined;
  }[] = [];

  gasInputs: {
    pdr_id: string;
    supplier_id: string;
    gasSupplierInput: string;
    gasVatInput: string;
    pdrIdInput: string;
    gasStartOfSupplyDate: Date | undefined;
    remiInput: string;
    gasEndOfSupplyDate: Date | undefined;
  }[] = [];

  // State of page
  successful: boolean = false;

  // Dialog error
  dialogErrorMessage: string | null = null;

  // Button click
  confirmClicked: boolean = false;

  // Contains current location data, or null if creating new
  editLocation: Location | null = null;
  invoiceElectricity: ElectricityInvoice | null = null;
  invoiceGas: GasInvoice | null = null;

  // Callback function for reloading locations
  @Input() reloadLocationsCallbackFunction!: () => void;
  @Input() deleteCallbackFunction!: (location: Location) => void;

  // Toast
  @Input() toast!: ToastComponent;

  @ViewChild(CreateLocationDialogComponent) confirmCreateLocation!: CreateLocationDialogComponent;

  constructor(private commonValidator: CommonValidatorService, private locationService: LocationService, private dateConversion: DateConversionService, private apiError: ApiErrorService, private translocoService: TranslocoService, private invoiceService: InvoiceService) { }

  openModal(editLocation?: Location, invoiceElectricity?: ElectricityInvoice, invoiceGas?: GasInvoice): void {
    this.visible = true;

    this.editLocation = editLocation ?? null;
    this.invoiceElectricity = invoiceElectricity ?? null;
    this.invoiceGas = invoiceGas ?? null;
    this.resetDialogData();
    this.populateInputsFromLocation(this.editLocation, this.invoiceElectricity, this.invoiceGas);
  }

  closeModal(): void {
    this.visible = false;
  }

  resetDialogData() {
    this.streetInput = this.streetNumberInput = this.postalCodeInput =
      this.cityInput = this.provinceInput = '';

    this.streetInputError = this.streetNumberInputError = this.postalCodeInputError =
      this.cityInputError = this.provinceInputError = null;

    this.electricityInputs = this.gasInputs = [];

    this.successful = false;
    this.dialogErrorMessage = null;
    this.confirmClicked = false;
  }

  // Returs false in case of error
  validateInputs(): boolean {
    const streetInputValue = this.streetInput.trim();
    const streetNumberInputValue = this.streetNumberInput.trim();
    const postalCodeInputValue = this.postalCodeInput.trim();
    const cityInputValue = this.cityInput.trim();
    const provinceInputValue = this.provinceInput.trim();

    // Validate length for text inputs
    const streetError = this.commonValidator.validateLength(streetInputValue, 2, 255);
    const streetNumberError = this.commonValidator.validateLength(streetNumberInputValue, 1, 10);
    const postalCodeError = this.commonValidator.validateLength(postalCodeInputValue, 1, 10);
    const cityError = this.commonValidator.validateLength(cityInputValue, 2, 255);
    const provinceError = this.commonValidator.validateLength(provinceInputValue, 2, 255);

    // Show errors
    this.streetInputError = streetError;
    this.streetNumberInputError = streetNumberError;
    this.postalCodeInputError = postalCodeError;
    this.cityInputError = cityError;
    this.provinceInputError = provinceError;

    // Validate dates
    for (let i = 0; i < this.gasInputs.length; i++) {
      const gasInput = this.gasInputs[i];
      const startDate = gasInput.gasStartOfSupplyDate;
      const endDate = gasInput.gasEndOfSupplyDate;

      if (startDate && endDate) {
        if (endDate < startDate) {
          this.dialogErrorMessage = this.translocoService.translate('locations.end_date_error') + ` (PDR ${i + 1})`;
          return false;
        }
      }
    }

    for (let i = 0; i < this.electricityInputs.length; i++) {
      const gasInput = this.electricityInputs[i];
      const startDate = gasInput.startOfSupplyDate;
      const endDate = gasInput.endOfSupplyDate;

      if (startDate && endDate) {
        if (endDate < startDate) {
          this.dialogErrorMessage = this.translocoService.translate('locations.end_date_error') + ` (POD ${i + 1})`;
          return false;
        }
      }
    }

    // If there are any errors show them and prevent submission
    if (
      streetError || streetNumberError || postalCodeError || cityError || provinceError
    ) {
      return false;
    }

    return true;
  }

  populateLocation(id: string, location_data_id: string | null = null, location_gas_data_id: string | null = null, is_active: boolean = true): Location {
    const firstPod = this.electricityInputs?.[0];
    const firstPDR = this.gasInputs?.[0];

    const newLocation: Location = {
      id: id,
      title: null,
      street: this.streetInput.trim(),
      number: this.streetNumberInput.trim(),
      postal_code: this.postalCodeInput.trim(),
      city: this.cityInput.trim(),
      province: this.provinceInput.trim(),
      latitude: null,
      longitude: null,
      is_active: is_active,
      pods: firstPod ? [{
        supplier: firstPod.supplierInput?.trim() || null,
        excise_taxes: firstPod.exciseTaxesInput?.trim() || null,
        pod_id: firstPod.podIdInput?.trim() || null,
        vat: parseFloat(firstPod.vatInput?.trim()) || null,
        available_capacity: parseFloat(firstPod.availableCapacityInput?.trim()) || null,
        start_of_supply: !firstPod.startOfSupplyDate
          ? null
          : this.dateConversion.dateToIso(firstPod.startOfSupplyDate) || null,
        tension: firstPod.tensionInput?.trim() || null,
        end_of_supply: !firstPod.endOfSupplyDate
          ? null
          : this.dateConversion.dateToIso(firstPod.endOfSupplyDate) || null,
        location_id: id,
        id: location_data_id
      }] : [],
      pdrs: firstPDR ? [{
        supplier: firstPDR.gasSupplierInput.trim() ?? null,
        vat: parseFloat(firstPDR.gasVatInput.trim()) || null,
        pdr_id: firstPDR.pdrIdInput.trim() ?? null,
        start_of_supply: !firstPDR.gasStartOfSupplyDate ? null : this.dateConversion.dateToIso(firstPDR.gasStartOfSupplyDate) || null,
        remi: firstPDR.remiInput.trim() ?? null,
        end_of_supply: !firstPDR.gasEndOfSupplyDate ? null : this.dateConversion.dateToIso(firstPDR.gasEndOfSupplyDate) || null,
        location_id: id,
        id: location_gas_data_id
      }] : []
    };
    return newLocation;
  }

  async confirmButtonClick() {
    if (this.confirmClicked) {
      return;
    }
    this.confirmClicked = true;

    if (!this.validateInputs()) {
      this.confirmClicked = false;
      return;
    }

    var dialogResponse = await this.confirmCreateLocation.openModal();
    if (!dialogResponse) {
      this.confirmClicked = false;
      return;
    }

    // Create location
    const newLocation: Location = this.populateLocation("");
    var responseNewLocation: BaseLocation | undefined;

    // Send request for creating location
    try {
      responseNewLocation = await firstValueFrom(this.locationService.createLocation(newLocation));
    } catch (error) {
      var crid = 'UNKNOWN-CRID';
      if (error instanceof HttpErrorResponse) {
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
      }
      this.dialogErrorMessage = `${await this.apiError.getErrorHeader()} ${await this.apiError.getErrorMessage(crid)}`;
      this.confirmClicked = false;
      return;
    }

    this.dialogErrorMessage = null;
    this.successful = true;
    this.confirmClicked = false;

    // Add location electricity and gas data
    if (!responseNewLocation) {
      if (this.reloadLocationsCallbackFunction) {
        this.reloadLocationsCallbackFunction();
      }
      return;
    }

    try {
      newLocation.pods![0].location_id = responseNewLocation.id;
      await firstValueFrom(this.locationService.createLocationPOD(newLocation.pods![0]));
    } catch (error) {
      // In case of empty request validation error
    }

    try {
      newLocation.pdrs![0].location_id = responseNewLocation.id;
      await firstValueFrom(this.locationService.createLocationPDR(newLocation.pdrs![0]));
    } catch (error) {
      // In case of empty request validation error
    }

    if (this.reloadLocationsCallbackFunction) {
      this.reloadLocationsCallbackFunction();
    }
  }

  populateInputsFromLocation(editLocation: Location | null, invoiceElectricity: ElectricityInvoice | null, invoiceGas: GasInvoice | null): void {
    if (!editLocation) {
      return;
    }

    // Address Information
    this.streetInput = editLocation.street ?? '';
    this.streetNumberInput = editLocation.number ?? '';
    this.postalCodeInput = editLocation.postal_code ?? '';
    this.cityInput = editLocation.city ?? '';
    this.provinceInput = editLocation.province ?? '';

    // Electricity
    this.electricityInputs = (invoiceElectricity?.pods ?? []).map(p => ({
      pod_id: p.id || '',
      supplier_id: p.supplier?.id || '',
      supplierInput: p.supplier?.name || '',
      exciseTaxesInput: p.excise_taxes?.toString() || '',
      podIdInput: p.pod_id?.toString() || '',
      vatInput: p.vat_rate?.toString() || '',
      availableCapacityInput: p.power_kw_available?.toString() || '',
      startOfSupplyDate: p.supplier?.start_of_supply ? new Date(p.supplier.start_of_supply) : undefined,
      tensionInput: p.network_tension?.toString() || '',
      endOfSupplyDate: p.supplier?.end_of_supply ? new Date(p.supplier.end_of_supply) : undefined
    }));

    // Gas
    this.gasInputs = (invoiceGas?.pdrs ?? []).map(p => ({
      pdr_id: p.id || '',
      supplier_id: p.supplier?.id || '',
      gasSupplierInput: p.supplier?.name || '',
      pdrIdInput: p.pdr_id?.toString() || '',
      gasVatInput: p.vat_rate?.toString() || '',
      gasStartOfSupplyDate: p.supplier?.start_of_supply ? new Date(p.supplier.start_of_supply) : undefined,
      remiInput: p.REMI_type_gas?.toString() || '',
      gasEndOfSupplyDate: p.supplier?.end_of_supply ? new Date(p.supplier.end_of_supply) : undefined
    }));
  }

  async saveButtonClick() {
    if (this.confirmClicked) {
      return;
    }
    this.confirmClicked = true;

    if (!this.validateInputs()) {
      this.confirmClicked = false;
      return;
    }

    const podId = this.editLocation?.pods?.[0]?.id ?? null;
    const pdrId = this.editLocation?.pdrs?.[0]?.id ?? null;

    // Edit location
    const editLocation: Location = this.populateLocation(this.editLocation!.id, podId, pdrId ?? null, this.editLocation?.is_active);

    // Send request for creating location
    try {
      await firstValueFrom(this.locationService.updateLocation(editLocation));
      if (podId) {
        await firstValueFrom(this.locationService.updateLocationPOD(editLocation.pods![0]));
      }
      else {
        try {
          editLocation.pods![0].location_id = editLocation.id;
          await firstValueFrom(this.locationService.createLocationPOD(editLocation.pods![0]));
        } catch (error) {
          // In case of empty request validation error
        }
      }


      if (pdrId) {
        await firstValueFrom(this.locationService.updateLocationPDR(editLocation.pdrs![0]));
      }
      else {
        try {
          editLocation.pdrs![0].location_id = editLocation.id;
          await firstValueFrom(this.locationService.createLocationPDR(editLocation.pdrs![0]));
        } catch (error) {
          // In case of empty request validation error
        }
      }
    } catch (error) {
      var crid = 'UNKNOWN-CRID';
      if (error instanceof HttpErrorResponse) {
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
      }
      this.dialogErrorMessage = `${await this.apiError.getErrorHeader()} ${await this.apiError.getErrorMessage(crid)}`;
      this.confirmClicked = false;
      return;
    }

    // Update POD/PDR data
    try {
      // PODs
      for (let i = 0; i < this.electricityInputs.length; i++) {
        const pod = this.electricityInputs[i];
        const pod_id = pod.pod_id;

        const updateData: LocationViewUpdatePOD = {
          pod_id: pod.podIdInput,
          excise_taxes: parseFloat(pod.exciseTaxesInput ?? '') ?? null,
          available_capacity: parseFloat(pod.availableCapacityInput ?? '') ?? null,
          tension: pod.tensionInput,
          supplier_id: pod.supplier_id,
          supplier: pod.supplierInput,
          vat_rate: parseFloat(pod.vatInput ?? '') ?? null,
          start_of_supply: !pod.startOfSupplyDate
            ? null
            : this.dateConversion.dateToIso(pod.startOfSupplyDate) || null,
          end_of_supply: !pod.endOfSupplyDate
            ? null
            : this.dateConversion.dateToIso(pod.endOfSupplyDate) || null
        };

        await lastValueFrom(this.invoiceService.updateElectricityLocationPod(pod_id, updateData))
      }

      // PDRs
      for (let i = 0; i < this.gasInputs.length; i++) {
        const pdr = this.gasInputs[i];
        const pdr_id = pdr.pdr_id;

        const updateData: LocationViewUpdatePDR = {
          pdr_id: pdr.pdrIdInput,
          supplier_id: pdr.supplier_id,
          supplier: pdr.gasSupplierInput,
          remi: pdr.remiInput,
          vat_rate: parseFloat(pdr.gasVatInput ?? '') ?? null,
          start_of_supply: !pdr.gasStartOfSupplyDate
            ? null
            : this.dateConversion.dateToIso(pdr.gasStartOfSupplyDate) || null,
          end_of_supply: !pdr.gasEndOfSupplyDate
            ? null
            : this.dateConversion.dateToIso(pdr.gasEndOfSupplyDate) || null
        };

        await lastValueFrom(this.invoiceService.updateGasLocationPdr(pdr_id, updateData))
      }

      if (this.reloadLocationsCallbackFunction) {
        this.reloadLocationsCallbackFunction();
      }
    } catch (error) {
      var crid = 'UNKNOWN-CRID';
      if (error instanceof HttpErrorResponse) {
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
      }
      this.dialogErrorMessage = `${await this.apiError.getErrorHeader()} ${await this.apiError.getErrorMessage(crid)}`;
      this.confirmClicked = false;
      return;
    }

    this.dialogErrorMessage = null;
    this.successful = true;
    this.confirmClicked = false;
  }

  deleteLocation() {
    this.closeModal();
    if (this.deleteCallbackFunction) {
      this.deleteCallbackFunction(this.editLocation!);
    }
  }
}
